import { apiGet, apiPatch, apiPost, apiUploadPut } from '../client';
import { z } from 'zod';
import {
  MenuPhotoUploadSchema,
  MenuProductAnalysisResponseSchema,
  MenuProductListResponseSchema,
  MenuProductSchema,
  type MenuPhotoUpload,
  type MenuProduct,
  type MenuProductProposal,
} from '../schemas';

const MenuProductResponseSchema = z.object({ data: MenuProductSchema });
const MenuPhotoUploadResponseSchema = z.object({ data: MenuPhotoUploadSchema });

export function listMenuProducts(includeInactive: boolean): Promise<{ data: MenuProduct[] }> {
  return apiGet(
    `/v1/business/menu-products${includeInactive ? '?includeInactive=true' : ''}`,
    MenuProductListResponseSchema,
  );
}

export type CreateMenuProductInput = {
  name: string;
  category: string;
  priceCents: number;
  sortOrder?: number;
};

export function createMenuProduct(input: CreateMenuProductInput, idempotencyKey: string): Promise<MenuProduct> {
  return apiPost('/v1/business/menu-products', MenuProductResponseSchema, input, idempotencyKey).then((result) => result.data);
}

export type PatchMenuProductInput = Partial<CreateMenuProductInput> & {
  isActive?: boolean;
  /** The version the form read; a stale one comes back 409 version_conflict. */
  expectedVersion?: number;
};

export function patchMenuProduct(
  productId: string,
  input: PatchMenuProductInput,
  idempotencyKey: string,
): Promise<MenuProduct> {
  return apiPatch(
    `/v1/business/menu-products/${encodeURIComponent(productId)}`,
    MenuProductResponseSchema,
    input,
    idempotencyKey,
  ).then((result) => result.data);
}

export type PutMenuPhotoInput = { blob: Blob; width: number; height: number };

export function putMenuPhoto(input: PutMenuPhotoInput, idempotencyKey: string): Promise<MenuPhotoUpload> {
  const form = new FormData();
  form.append('image', input.blob, 'menu.jpg');
  form.append('meta', JSON.stringify({ width: input.width, height: input.height }));
  return apiUploadPut('/v1/business/menu-photo', MenuPhotoUploadResponseSchema, form, idempotencyKey).then((result) => result.data);
}

export function analyzeMenuPhoto(objectKey: string): Promise<MenuProductProposal[]> {
  return apiPost('/v1/business/menu-products/analyze', MenuProductAnalysisResponseSchema, { objectKey }).then(
    (result) => result.data.products,
  );
}
